import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { InstanceSetupStatusDto } from '@/services/aiStudioInstanceSetupService';
import type { InstanceSetupSchema } from '@/types/aiStudioAgent';
import { hasSetupFieldValue } from './setupFieldValues';

interface InstanceSetupReviewStepProps {
  status: InstanceSetupStatusDto;
  schema?: InstanceSetupSchema | null;
  fieldValues?: Record<string, unknown>;
  saving: boolean;
  onBack: () => void;
  onComplete: () => void;
  onGoToInstance: () => void;
}

export const InstanceSetupReviewStep: React.FC<InstanceSetupReviewStepProps> = ({
  status,
  schema,
  fieldValues = {},
  saving,
  onBack,
  onComplete,
  onGoToInstance,
}) => {
  const { t } = useLanguage();
  const ready = status.status === 'ready';
  const provisionFields = schema?.provisionFields ?? [];

  const formatValue = (type: string, key: string, value: unknown): string => {
    if (type === 'secret') return value ? '••••••••' : '—';
    if (type === 'file' || key === 'scheduling_working_set') {
      return hasSetupFieldValue('file', key, value)
        ? t('aiStudio.instanceSetup.fieldFileUploaded')
        : '—';
    }
    if (type === 'boolean') {
      return value === true ? t('aiStudio.common.yes') : t('aiStudio.common.no');
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    if (typeof value === 'string' && value.trim()) return value;
    if (value !== undefined && value !== null && value !== '') return String(value);
    return '—';
  };

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.instanceSetup.reviewTitle')}
      </h2>
      <p className="mt-1 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.instanceSetup.reviewHint')}
      </p>

      {provisionFields.length > 0 ? (
        <dl className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {provisionFields.map((field) => (
            <div key={field.key}>
              <dt className="text-base text-[var(--color-surface-text-secondary)]">{field.label}</dt>
              <dd className="text-base text-[var(--color-surface-text)] break-words">
                {formatValue(field.type, field.key, fieldValues[field.key])}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <dl className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.instanceSetup.siteName')}
            </dt>
            <dd className="text-base text-[var(--color-surface-text)]">
              {status.summary.siteName || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.instanceSetup.summaryResources')}
            </dt>
            <dd className="text-base text-[var(--color-surface-text)]">
              {status.summary.resourceCount}
            </dd>
          </div>
          <div>
            <dt className="text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.instanceSetup.summaryDemands')}
            </dt>
            <dd className="text-base text-[var(--color-surface-text)]">
              {status.summary.demandCount}
            </dd>
          </div>
          <div>
            <dt className="text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.instanceSetup.summaryTasks')}
            </dt>
            <dd className="text-base text-[var(--color-surface-text)]">
              {status.summary.taskCount}
            </dd>
          </div>
        </dl>
      )}

      {status.messages.length > 0 && (
        <div className="mt-3">
          <h3 className="text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.instanceSetup.messagesTitle')}
          </h3>
          <ul className="mt-1 list-disc pl-5 text-base text-[var(--color-surface-text-secondary)]">
            {status.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!ready && (
          <>
            <button
              type="button"
              onClick={onBack}
              disabled={saving}
              className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
            >
              {t('aiStudio.instanceSetup.previous')}
            </button>
            <button
              type="button"
              onClick={() => void onComplete()}
              disabled={saving || !status.steps.fields}
              className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 disabled:opacity-50"
            >
              {saving ? t('aiStudio.common.saving') : t('aiStudio.instanceSetup.completeSetup')}
            </button>
          </>
        )}
        {ready && (
          <button
            type="button"
            onClick={onGoToInstance}
            className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90"
          >
            {t('aiStudio.instanceSetup.continueToInstance')}
          </button>
        )}
      </div>
    </section>
  );
};
